import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import config from '../config';
import { getUserIdFromUrl } from '../helper/apiHelper';
import { useTranslation } from '../hooks/useTranslation';
import { useStatic } from '../context/StaticContext';
import { useMaintenanceMode } from '../context/MaintenanceContext';
import SuccessAlert from './alerts/SuccessAlert';
import ErrorAlert from './alerts/ErrorAlert';
import WarningAlert from './alerts/WarningAlert';
import InfoAlert from './alerts/InfoAlert';

const BonusConfirmPopup = ({ isOpen, onClose, bonus, onConfirm }) => {
  const [loading, setLoading] = useState(false);
  const [showRedirectConfirm, setShowRedirectConfirm] = useState(false);
  const [redirectUrl, setRedirectUrl] = useState(null);
  const [imageError, setImageError] = useState(false);
  const [alertData, setAlertData] = useState({
    show: false,
    type: null, // 'success', 'error', 'warning', 'info'
    message: ''
  });
  const { t } = useTranslation();
  const { selectedLanguage, isLanguage } = useStatic();
  const { isMaintenanceMode } = useMaintenanceMode();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Mobil ekran boyutu kontrolü
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Dinamik çeviri fonksiyonu (MyMemory API kullanarak)
  const translateMessage = async (text, targetLang) => {
    // isLanguage yoksa veya false ise çevirme yapma
    if (!isLanguage || !targetLang || targetLang === 'tr-TR') {
      return text; // Türkçe ise veya dil desteği kapalıysa çevirme
    }

    try {
      // Dil kodunu API formatına çevir (tr-TR -> tr|en)
      const langMap = {
        'en-US': 'tr|en',
        'de-DE': 'tr|de', 
        'fr-FR': 'tr|fr',
        'es-ES': 'tr|es',
        'it-IT': 'tr|it',
        'pt-PT': 'tr|pt'
      };

      const langPair = langMap[targetLang];
      if (!langPair) return text;

      const response = await fetch(
        `https://api.mymemory.translated.net/get?q=${encodeURIComponent(text)}&langpair=${langPair}`
      );
      
      const data = await response.json();
      
      if (data.responseStatus === 200 && data.responseData.translatedText) {
        return data.responseData.translatedText;
      }
      
      return text; // Çeviri başarısızsa orijinal metni döndür
    } catch (error) {
      console.warn('Translation failed:', error);
      return text; // Hata durumunda orijinal metni döndür
    }
  };

  // URL kontrolü için yardımcı fonksiyon
  const isValidUrl = (string) => {
    if (!string || typeof string !== 'string') return false;
    try {
      const url = new URL(string);
      return url.protocol === 'http:' || url.protocol === 'https:';
    } catch (_) {
      return false;
    }
  };

  // Popup kapandığında state'leri temizle
  useEffect(() => {
    if (!isOpen) {
      setShowRedirectConfirm(false);
      setRedirectUrl(null);
      setLoading(false);
      setImageError(false);
      setAlertData({ show: false, type: null, message: '' });
    }
  }, [isOpen]);

  // Modal açıkken #root scroll'unu kilitle
  useEffect(() => {
    const root = document.getElementById('root');
    if (!root) return;
    if (isOpen) {
      root.style.overflow = 'hidden';
    }
    return () => {
      root.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  // Yönlendirme onayı
  const handleRedirectConfirm = () => {
    if (redirectUrl) {
      window.open(redirectUrl, '_blank');
      setShowRedirectConfirm(false);
      setRedirectUrl(null);
      onClose();
    }
  };

  const handleRedirectCancel = () => {
    setShowRedirectConfirm(false);
    setRedirectUrl(null);
  };

  const handleConfirmBonus = async () => {
    // Eğer zaten işlem yapılıyorsa, tekrar istek atma
    if (loading) {
      return;
    }

    // Eğer bonus verisinde redirect_link varsa ve geçerli bir URL ise, onay mesajı göster
    if (bonus.redirect_link && isValidUrl(bonus.redirect_link)) {
      setRedirectUrl(bonus.redirect_link);
      setShowRedirectConfirm(true);
      return;
    }
    setLoading(true);
    try {
      const payload = {
        process_id: bonus.process_name,
        user_id: getUserIdFromUrl(),
        recaptcha: "test"
      };

      // Bakım modu kontrolü - Direkt URL'den kontrol et (daha güvenilir)
      const queryParams = new URLSearchParams(window.location.search);
      const bakim = queryParams.get('bakim');
      if (bakim === 't' || isMaintenanceMode) {
        payload.dev = "10";
      }

      let body = JSON.stringify(payload);
      let keys = await window.rh.cr(body);
      const response = await fetch(`${config.apiBaseUrl2}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          "hk": keys[0],
          "hh": keys[1]
        },
        body: body,
      });

      const data = await response.json();

      // Eğer API yanıtında redirect_link varsa ve geçerli bir URL ise, onay mesajı göster
      if (data.redirect_link && isValidUrl(data.redirect_link)) {
        setRedirectUrl(data.redirect_link);
        setLoading(false);
        setShowRedirectConfirm(true);
        return;
      }
      
      // API yanıtına göre alert türünü ve mesajını belirle
      let messageToTranslate = '';
      let alertType = '';
      
      if (data.status === true) {
        messageToTranslate = data.message || 'Bonus talebi başarıyla onaylandı!';
        alertType = 'success';
      } else if (data.status === 3) {
        messageToTranslate = data.message || 'Bonus talebiniz için bir uyarı mevcut.';
        alertType = 'warning';
      } else if (data.status === 4) {
        messageToTranslate = data.message || 'Bonus talebiniz hakkında bilgilendirme.';
        alertType = 'info';
      } else {
        messageToTranslate = data.message || 'Bonus talebiniz onaylanamadı.';
        alertType = 'error';
      }

      // Mesajı seçili dile çevir (isLanguage kontrolü translateMessage fonksiyonunda)
      const translatedMessage = await translateMessage(messageToTranslate, selectedLanguage);

      setAlertData({
        show: true,
        type: alertType,
        message: translatedMessage
      });
      
      // Alert gösterildikten 3 saniye sonra popup'ı kapat
      setTimeout(() => {
        onClose();
        // Alert'i kapat
        setTimeout(() => {
          setAlertData({ show: false, type: null, message: '' });
        }, 500);
      }, 3000);
      
    } catch (error) {
      console.error('Bonus onayı sırasında hata oluştu:', error);
      
      // Hata mesajını da çevir
      const errorMessage = 'Bir hata oluştu. Lütfen daha sonra tekrar deneyin.';
      const translatedErrorMessage = await translateMessage(errorMessage, selectedLanguage);
      
      setAlertData({
        show: true,
        type: 'error',
        message: translatedErrorMessage
      });
    } finally {
      setLoading(false);
    }
  };

  // Alert tipine göre uygun bileşeni göster
  const renderAlert = () => {
    if (!alertData.show) return null;
    
    switch (alertData.type) {
      case 'success':
        return <SuccessAlert message={alertData.message} />;
      case 'warning':
        return <WarningAlert message={alertData.message} />;
      case 'info':
        return <InfoAlert message={alertData.message} />;
      case 'error':
      default:
        return <ErrorAlert message={alertData.message} />;
    }
  };

  // Mobil için dinamik stiller (sadece mobilde uygulanır)
  const mobilePopupStyle = isMobile ? {
    maxWidth: '340px',
  } : {};

  const mobileContentStyle = isMobile ? {
    gap: '12px'
  } : {};

  const mobileTitleStyle = isMobile ? {
    fontSize: '20px'
  } : {};

  const mobileImageStyle = isMobile ? {
    maxWidth: '180px',
  } : {};

  const mobileQuestionStyle = isMobile ? {
    fontSize: '14px'
  } : {};

  const mobileButtonStyle = isMobile ? {
    padding: '10px 0',
    fontSize: '14px'
  } : {};

  return createPortal(
    <>
      {alertData.show && createPortal(renderAlert(), document.body)}
      <div className="bonus-confirm-overlay">
        <div className="bonus-confirm-popup" style={mobilePopupStyle}>
        <div className="bonus-confirm-header">
          <button 
            className="close-button"
            onClick={onClose}
          >
            ✕
          </button>
        </div>
        
        {showRedirectConfirm ? (
          // Yönlendirme onay mesajı
          <>
            <div className="bonus-confirm-content" style={mobileContentStyle}>
              <h3 style={mobileTitleStyle}>{bonus.title}</h3>
              
              <div className="bonus-confirm-image-container" style={mobileImageStyle}>
                <img 
                  src={bonus.image} 
                  alt={bonus.title} 
                  className="bonus-confirm-image"
                  onError={(e) => {
                    console.error('Resim yüklenemedi:', e.target.src);
                  }}
                />
              </div>
              
              <p className="bonus-confirm-question" style={{ 
                marginTop: isMobile ? '10px' : '20px', 
                marginBottom: '10px',
                ...mobileQuestionStyle 
              }}>
                {t('popup.redirectConfirm', 'Yönlendiriliyorsunuz, emin misiniz?')}
              </p>
              
              {redirectUrl && (
                <div className="bonus-confirm-redirect-link-container">
                  <strong className="bonus-confirm-redirect-link-label">Link:</strong>
                  <br />
                  <a 
                    href={redirectUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="bonus-confirm-redirect-link"
                  >
                    {redirectUrl}
                  </a>
                </div>
              )}
            </div>
            
            <div className="bonus-confirm-actions">
              <button 
                className="bonus-cancel-button" 
                onClick={handleRedirectCancel}
                style={mobileButtonStyle}
              >
                {t('buttons.cancel', 'Vazgeç')}
              </button>
              <button 
                className="bonus-confirm-button" 
                onClick={handleRedirectConfirm}
                style={mobileButtonStyle}
              >
                {t('buttons.confirm', 'Evet, Yönlendir')}
              </button>
            </div>
          </>
        ) : (
          // Normal bonus onay ekranı
          <>
        <div className="bonus-confirm-content" style={mobileContentStyle}>
          <h3 style={mobileTitleStyle}>{bonus.title}</h3>
          
              <div className="bonus-confirm-image-container" style={mobileImageStyle}>
                <img 
                  src={bonus.image} 
                  alt={bonus.title} 
                  className="bonus-confirm-image"
                  onError={(e) => {
                    console.error('Resim yüklenemedi:', e.target.src);
                  }}
                />
              </div>
          
          <p className="bonus-confirm-question" style={mobileQuestionStyle}>
            {bonus.talep_text || t('popup.confirmTitle', 'Bu bonusu talep etmek istediğinizden emin misiniz?')}
          </p>
        </div>
        
        <div className="bonus-confirm-actions">
        <button 
            className="bonus-confirm-button" 
            onClick={handleConfirmBonus}
            disabled={loading}
            style={mobileButtonStyle}
          >
            {loading ? t('buttons.processing', 'İşleniyor...') : t('buttons.request', 'Talep Et')}
          </button>
          <button 
            className="bonus-cancel-button" 
            onClick={onClose}
            disabled={loading}
            style={mobileButtonStyle}
          >
            {t('buttons.cancel', 'Vazgeç')}
          </button>
          
        </div>
          </>
        )}
        </div>
      </div>
    </>,
    document.body
  );
};

export default BonusConfirmPopup; 